import type { ComponentPropsWithoutRef, ReactNode } from 'react';

/** Semantic badge color variants for status and emphasis. */
export type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

/** Fill emphasis — soft wash (default), solid fill, or outline. */
export type BadgeEmphasis = 'soft' | 'solid' | 'outline';

const VARIANT_CLASS: Record<BadgeVariant, string> = {
  neutral: 'badge--neutral',
  success: 'badge--success',
  warning: 'badge--warning',
  danger: 'badge--danger',
  info: 'badge--info',
  accent: 'badge--accent',
};

const EMPHASIS_CLASS: Record<BadgeEmphasis, string | undefined> = {
  soft: undefined,
  solid: 'badge--solid',
  outline: 'badge--outline',
};

export type BadgeProps = ComponentPropsWithoutRef<'span'> & {
  children: ReactNode;
  variant?: BadgeVariant;
  /** Fill style. Soft is the default wash treatment. */
  emphasis?: BadgeEmphasis;
  /** Optional leading status dot. */
  dot?: boolean;
};

/** Inline status label using theme badge utility classes. */
export function Badge({
  children,
  variant = 'neutral',
  emphasis = 'soft',
  dot = false,
  className,
  ...props
}: BadgeProps): React.JSX.Element {
  const classes = ['badge', VARIANT_CLASS[variant], EMPHASIS_CLASS[emphasis], className]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} {...props}>
      {dot ? <span className="badge__dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}
